import React, { useMemo, useState } from 'react';
import { IntegrationVendor } from '../../models/enums/integrationEnums';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { useEnumValues } from '../../hooks/useEnumValues';

interface VendorOverrideFieldsProps {
  vendor: string;
  overrideValues: Record<string, string>;
  onValueChange: (field: string, value: string) => void;
  onRemoveValue?: (field: string) => void;
  overrideMode?: boolean;
  overrideFlags?: Record<string, boolean>;
  onOverrideFlagChange?: (field: string, checked: boolean) => void;
}

export const VendorOverrideFields = ({ 
  vendor, 
  overrideValues, 
  onValueChange,
  onRemoveValue,
  overrideMode = false,
  overrideFlags = {},
  onOverrideFlagChange
}: VendorOverrideFieldsProps) => {
  const [visibleFields, setVisibleFields] = useState<Record<string, boolean>>({});
  const toggleFieldVisibility = (fieldKey: string) => {
    setVisibleFields((prev) => ({ ...prev, [fieldKey]: !prev[fieldKey] }));
  };

  const isExperian = vendor === IntegrationVendor.Experian;
  const { data: enumValues, isError: isErrorPurposes } = useEnumValues('Core.ThirdParty.IncomeEmploymentAsset.Vendors.Experian.Models.PermissiblePurposeCode', isExperian);
  const permissiblePurposes = useMemo(
    () => (enumValues ?? []).filter((v) => !v.isHidden).sort((a, b) => a.order - b.order),
    [enumValues]
  );

  // Build subscriber code rows from the flattened overrideValues
  const subscriberCodeRows = useMemo(() => {
    return Object.keys(overrideValues)
      .filter((key) => key.startsWith('subscriberCode_'))
      .map((key) => {
        const suffix = key.replace('subscriberCode_', '');
        return {
          suffix,
          purposeValue: suffix.startsWith('__new_') ? '' : suffix,
          codeValue: overrideValues[key] || '',
        };
      });
  }, [overrideValues]);

  const handleAddSubscriberCode = () => {
    const tempKey = '__new_' + Date.now();
    onValueChange('subscriberCode_' + tempKey, '');
  };

  const handlePurposeChange = (oldSuffix: string, newPurpose: string) => {
    const oldCodeKey = 'subscriberCode_' + oldSuffix;
    const existingCode = overrideValues[oldCodeKey] || '';
    const wasOverridden = overrideFlags[oldCodeKey] || false;

    // Remove old key
    if (onRemoveValue) {
      onRemoveValue(oldCodeKey);
    }
    if (onOverrideFlagChange) {
      onOverrideFlagChange(oldCodeKey, false);
    }

    // Set new key with the existing code value
    onValueChange('subscriberCode_' + newPurpose, existingCode);
    if (wasOverridden && onOverrideFlagChange) {
      onOverrideFlagChange('subscriberCode_' + newPurpose, true);
    }
  };

  const handleRemoveSubscriberCode = (suffix: string) => {
    const codeKey = 'subscriberCode_' + suffix;
    if (onRemoveValue) {
      onRemoveValue(codeKey);
    }
    if (onOverrideFlagChange) {
      onOverrideFlagChange(codeKey, false);
    }
  };

  // Build dropdown options, filtering out already-selected purposes
  const getAvailablePurposes = (currentSuffix: string) => {
    const otherSelected = subscriberCodeRows
      .filter((r) => r.suffix !== currentSuffix)
      .map((r) => r.purposeValue)
      .filter((v) => v !== '');
    return permissiblePurposes
      .filter((p) => !otherSelected.includes(p.name || ''))
      .map((p) => ({
        label: p.displayName || p.name || '',
        value: p.name || '',
      }));
  };

  const renderField = (
    id: string,
    label: string,
    fieldKey: string,
    placeholder: string,
    type: string = 'text'
  ) => {
    const isOverridden = overrideFlags[fieldKey] || false;
    const isDisabled = overrideMode && !isOverridden;
    const isPasswordField = type === 'password';
    const isVisible = visibleFields[fieldKey] || false;
    const inputType = isPasswordField && !isVisible ? 'password' : 'text';

    return (
      <div className="field mb-4">
        <div className="flex align-items-center justify-content-between mb-2">
          <label htmlFor={id}>{label}</label>
          {overrideMode && onOverrideFlagChange && (
            <div className="flex align-items-center gap-2">
              <Checkbox
                inputId={`${id}-override`}
                checked={isOverridden}
                onChange={(e) => onOverrideFlagChange(fieldKey, e.checked || false)}
              />
              <label htmlFor={`${id}-override`} className="text-sm mb-0">Override?</label>
            </div>
          )}
        </div>
        <div className="p-inputgroup">
          <input
            id={id}
            type={inputType}
            value={overrideValues[fieldKey] || ''}
            onChange={(e) => onValueChange(fieldKey, e.target.value)}
            className="p-inputtext p-component"
            style={{ width: '100%' }}
            placeholder={placeholder}
            disabled={isDisabled}
          />
          {isPasswordField && (
            <button
              type="button"
              className="p-inputgroup-addon"
              onClick={() => toggleFieldVisibility(fieldKey)}
              style={{ cursor: 'pointer', background: 'transparent', border: '1px solid var(--surface-border)' }}
              tabIndex={-1}
            >
              <i className={isVisible ? 'pi pi-eye-slash' : 'pi pi-eye'} />
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* RatesBoard - Branch Code */}
      {vendor === IntegrationVendor.RatesBoard && (
        <>
          {renderField('branchCode', 'Branch Code', 'branchCode', 'Enter branch code')}
        </>
      )}

      {/* Truv - Template ID, Client ID, Access Key */}
      {vendor === IntegrationVendor.Truv && (
        <>
          {renderField('templateId', 'Template ID', 'templateId', 'Enter template ID')}
          {renderField('clientId', 'Client ID', 'clientId', 'Enter client ID')}
          {renderField('accessKey', 'Access Key', 'accessKey', 'Enter access key', 'password')}
        </>
      )}

      {/* Argyle - API Key, API Secret */}
      {vendor === IntegrationVendor.Argyle && (
        <>
          {renderField('apiKey', 'API Key', 'apiKey', 'Enter API key')}
          {renderField('apiSecret', 'API Secret', 'apiSecret', 'Enter API secret', 'password')}
        </>
      )}

      {/* Equifax - User ID, Password */}
      {vendor === IntegrationVendor.Equifax && (
        <>
          {renderField('signOnRequestUserId', 'Sign-On Request User ID', 'signOnRequestUserId', 'Enter user ID')}
          {renderField('signOnRequestUserPassword', 'Sign-On Request User Password', 'signOnRequestUserPassword', 'Enter password', 'password')}
        </>
      )}

      {/* Vesta - Base URL, API Key */}
      {vendor === IntegrationVendor.Vesta && (
        <>
          {renderField('baseUrl', 'Base URL', 'baseUrl', 'Enter base URL')}
          {renderField('vestaApiKey', 'API Key', 'apiKey', 'Enter API key', 'password')}
        </>
      )}

      {/* Snap - Account ID, Site ID, Product ID */}
      {vendor === IntegrationVendor.Snap && (
        <>
          {renderField('accountId', 'Account ID', 'accountId', 'Enter account ID')}
          {renderField('siteId', 'Site ID', 'siteId', 'Enter site ID')}
          {renderField('productId', 'Product ID', 'productId', 'Enter product ID')}
        </>
      )}

      {/* FannieMae - Lender ID */}
      {vendor === IntegrationVendor.FannieMae && (
        <>
          {renderField('lenderId', 'Lender ID', 'lenderId', 'Enter lender ID')}
        </>
      )}

      {/* Gateless - Client ID, Client Secret */}
      {vendor === IntegrationVendor.Gateless && (
        <>
          {renderField('clientId', 'Client ID', 'clientId', 'Enter client ID')}
          {renderField('clientSecret', 'Client Secret', 'clientSecret', 'Enter client secret', 'password')}
        </>
      )}

      {/* SmartFees - API Key, Password, User ID, URL Override, Use V2 for Multi-Product */}
      {vendor === IntegrationVendor.SmartFees && (
        <>
          {renderField('apiKey', 'API Key', 'apiKey', 'Enter API key', 'password')}
          {renderField('password', 'Password', 'password', 'Enter password', 'password')}
          {renderField('userId', 'User ID', 'userId', 'Enter user ID')}
          {renderField('urlOverride', 'URL Override', 'urlOverride', 'Enter URL override (optional)')}
          {(() => {
            const fieldKey = 'useV2ForMultiProduct';
            const isOverridden = overrideFlags[fieldKey] || false;
            const isDisabled = overrideMode && !isOverridden;
            return (
              <div className="field mb-4">
                <div className="flex align-items-center justify-content-between mb-2">
                  <div className="flex align-items-center gap-2">
                    <Checkbox
                      inputId="useV2ForMultiProduct"
                      checked={String(overrideValues[fieldKey]) === 'true'}
                      onChange={(e) => onValueChange(fieldKey, e.checked ? 'true' : 'false')}
                      disabled={isDisabled}
                    />
                    <label htmlFor="useV2ForMultiProduct">Use V2 Endpoint for Multi-Product Requests</label>
                  </div>
                  {overrideMode && onOverrideFlagChange && (
                    <div className="flex align-items-center gap-2">
                      <Checkbox
                        inputId="useV2ForMultiProduct-override"
                        checked={isOverridden}
                        onChange={(e) => onOverrideFlagChange(fieldKey, e.checked || false)}
                      />
                      <label htmlFor="useV2ForMultiProduct-override" className="text-sm mb-0">Override?</label>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
          {(() => {
            const fieldKey = 'defaultToPreQualForMultiProduct';
            const isOverridden = overrideFlags[fieldKey] || false;
            const isDisabled = overrideMode && !isOverridden;
            return (
              <div className="field mb-4">
                <div className="flex align-items-center justify-content-between mb-2">
                  <div className="flex align-items-center gap-2">
                    <Checkbox
                      inputId="defaultToPreQualForMultiProduct"
                      checked={String(overrideValues[fieldKey]) === 'true'}
                      onChange={(e) => onValueChange(fieldKey, e.checked ? 'true' : 'false')}
                      disabled={isDisabled}
                    />
                    <label htmlFor="defaultToPreQualForMultiProduct">Default to PreQual for Multi-Product Requests</label>
                  </div>
                  {overrideMode && onOverrideFlagChange && (
                    <div className="flex align-items-center gap-2">
                      <Checkbox
                        inputId="defaultToPreQualForMultiProduct-override"
                        checked={isOverridden}
                        onChange={(e) => onOverrideFlagChange(fieldKey, e.checked || false)}
                      />
                      <label htmlFor="defaultToPreQualForMultiProduct-override" className="text-sm mb-0">Override?</label>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </>
      )}

      {/* Plaid - Client ID, Secret */}
      {vendor === IntegrationVendor.Plaid && (
        <>
          {renderField('clientId', 'Client ID', 'clientId', 'Enter client ID')}
          {renderField('secret', 'Secret', 'secret', 'Enter secret', 'password')}
        </>
      )}

      {/* Empower - Client ID, Client Secret, RealEC Client ID, RealEC Client Secret, RealEC Source ID */}
      {vendor === IntegrationVendor.Empower && (
        <>
          {renderField('clientId', 'Client ID', 'clientId', 'Enter client ID')}
          {renderField('clientSecret', 'Client Secret', 'clientSecret', 'Enter client secret', 'password')}
          {renderField('realECClientId', 'RealEC Client ID', 'realECClientId', 'Enter RealEC client ID')}
          {renderField('realECClientSecret', 'RealEC Client Secret', 'realECClientSecret', 'Enter RealEC client secret', 'password')}
          {renderField('realECSourceId', 'RealEC Source ID', 'realECSourceId', 'Enter RealEC source ID')}
        </>
      )}

      {/* Ernst - Username, Password */}
      {vendor === IntegrationVendor.Ernst && (
        <>
          {renderField('username', 'Username', 'username', 'Enter username')}
          {renderField('password', 'Password', 'password', 'Enter password', 'password')}
        </>
      )}

      {/* Truework - Access Key, Webhook Token */}
      {vendor === IntegrationVendor.Truework && (
        <>
          {renderField('accessKey', 'Access Key', 'accessKey', 'Enter access key', 'password')}
          {renderField('webhookToken', 'Webhook Token', 'webhookToken', 'Enter webhook token', 'password')}
        </>
      )}

      {/* Factual - App Key, Username, Password */}
      {vendor === IntegrationVendor.Factual && (
        <>
          {renderField('appKey', 'App Key', 'appKey', 'Enter app key')}
          {renderField('username', 'Username', 'username', 'Enter username')}
          {renderField('password', 'Password', 'password', 'Enter password', 'password')}
        </>
      )}

      {/* Experian - Account Identifier, Account Password, Client ID, Client Secret, Subscriber Codes per Permissible Purpose */}
      {vendor === IntegrationVendor.Experian && (
        <>
          {renderField('accountIdentifier', 'Account Identifier', 'accountIdentifier', 'Enter account identifier')}
          {renderField('accountPassword', 'Account Password', 'accountPassword', 'Enter account password', 'password')}
          {renderField('clientId', 'Client ID', 'clientId', 'Enter client ID')}
          {renderField('clientSecret', 'Client Secret', 'clientSecret', 'Enter client secret', 'password')}
          <h4 className="mt-2 mb-3">Subscriber Codes by Permissible Purpose</h4>
          {isErrorPurposes && (
            <div className="p-3 mb-3 border-round" style={{ backgroundColor: 'var(--red-50)', color: 'var(--red-700)' }}>
              <i className="pi pi-exclamation-triangle mr-2" />
              Unable to load permissible purposes. Subscriber code fields cannot be displayed.
            </div>
          )}
          {!isErrorPurposes && subscriberCodeRows.map((row) => {
            const codeKey = 'subscriberCode_' + row.suffix;
            const isOverridden = overrideFlags[codeKey] || false;
            const isDisabled = overrideMode && !isOverridden;

            return (
              <div key={row.suffix} className="flex align-items-start gap-2 mb-3">
                <div className="flex-1">
                  <div className="flex align-items-center justify-content-between mb-2">
                    <label className="font-medium">Permissible Purpose</label>
                    {overrideMode && onOverrideFlagChange && (
                      <div className="flex align-items-center gap-2">
                        <Checkbox
                          inputId={`${codeKey}-override`}
                          checked={isOverridden}
                          onChange={(e) => onOverrideFlagChange(codeKey, e.checked || false)}
                        />
                        <label htmlFor={`${codeKey}-override`} className="text-sm mb-0">Override?</label>
                      </div>
                    )}
                  </div>
                  <Dropdown
                    value={row.purposeValue}
                    options={getAvailablePurposes(row.suffix)}
                    onChange={(e) => handlePurposeChange(row.suffix, e.value)}
                    placeholder="Select Permissible Purpose"
                    disabled={isDisabled}
                    appendTo="self"
                    style={{ width: '100%' }}
                  />
                </div>
                <div className="flex-1">
                  <label className="block mb-2 font-medium">Subscriber Code</label>
                  <input
                    type="text"
                    value={row.codeValue}
                    onChange={(e) => onValueChange(codeKey, e.target.value)}
                    className="p-inputtext p-component"
                    style={{ width: '100%' }}
                    placeholder="Enter subscriber code"
                    disabled={isDisabled}
                  />
                </div>
                <Button
                  type="button"
                  icon="pi pi-trash"
                  className="p-button-danger p-button-text mt-4"
                  onClick={() => handleRemoveSubscriberCode(row.suffix)}
                  disabled={isDisabled}
                  style={{ minWidth: 'auto', width: 'auto' }}
                />
              </div>
            );
          })}
          {!isErrorPurposes && permissiblePurposes.length > 0 && subscriberCodeRows.length < permissiblePurposes.length && (
            <Button
              type="button"
              label="Add Purpose"
              icon="pi pi-plus"
              className="p-button-outlined mb-3"
              onClick={handleAddSubscriberCode}
              style={{ width: 'auto', minWidth: 'auto' }}
            />
          )}
        </>
      )}
    </>
  );
};
